// External library imports
import { useRouter } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  Eye,
  Highlighter,
  MoreHorizontal,
  Pencil,
  Star,
  Trash,
} from "lucide-react";

// Internal component imports
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Hooks and utilities
import { useConfirm } from "@/hooks/use-confirm";
import { cn } from "@/lib/utils";

import {
  useToggleTodoStarred,
  useToggleTodoStatus,
} from "../queries/todos.update.queries";
import { useDeleteTodo } from "../queries/todos.delete.queries";
import { TodoStatusEnum } from "@/types/todos.types";

interface TodoActionsProps {
  id: string;
  isStarred?: boolean;
  status?: string;
  optimisticOperation?: "create" | "update" | "delete";
}

const STATUS_OPTIONS = [
  { value: TodoStatusEnum.enum.todo, label: "Mark as Todo" },
  { value: TodoStatusEnum.enum.in_progress, label: "Mark as In Progress" },
  { value: TodoStatusEnum.enum.completed, label: "Mark as Completed" },
  { value: TodoStatusEnum.enum.archived, label: "Mark as Archived" },
] as const;

export const TodoActions = ({
  id,
  isStarred,
  status,
  optimisticOperation,
}: TodoActionsProps) => {
  const router = useRouter();
  const deleteTodo = useDeleteTodo();
  const toggleStarred = useToggleTodoStarred(id);
  const toggleStatus = useToggleTodoStatus(id);

  const [ConfirmationDialog, confirm] = useConfirm({
    title: "删除待办",
    message: "确定要删除该待办吗？此操作不可撤销。",
  });

  const handleView = () => {
    router.navigate({
      to: "/dashboard/todos/detail/$id",
      params: { id },
    });
  };

  const handleEdit = () => {
    router.navigate({
      to: "/dashboard/todos/edit/$id",
      params: { id },
    });
  };

  const handleDelete = async () => {
    if (optimisticOperation === "delete") {
      toast.success("待办删除成功");
      return;
    }

    try {
      const confirmed = await confirm();
      if (!confirmed) return;

      await deleteTodo.mutateAsync(id);
      toast.success("Todo deleted successfully");
    } catch (error) {
      console.error("删除待办错误:", error);
      toast.error("删除待办失败");
    }
  };

  const handleToggleStarred = async () => {
    try {
      await toggleStarred.mutateAsync(id);
      toast.success(`待办${isStarred ? "取消标注" : "标注为重要"}成功`);
    } catch (error) {
      console.error("切换重要状态错误:", error);
      toast.error("更新待办失败");
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      await toggleStatus.mutateAsync({ id, status: newStatus });
      toast.success("待办状态更新成功");
    } catch (error) {
      console.error("Toggle status error:", error);
      toast.error("Failed to update todo status");
    }
  };

  return (
    <div
      className="flex items-center gap-2"
      role="group"
      aria-label="Todo actions"
    >
      <div className="flex items-center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`当前状态：${status}`}
            >
              <Highlighter className="size-6" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {STATUS_OPTIONS.map(({ value, label }) => (
              <DropdownMenuItem
                key={value}
                disabled={toggleStatus.isPending}
                onClick={() => handleStatusChange(value)}
              >
                {label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleToggleStarred}
              aria-label={`${isStarred ? "Remove from" : "Mark as"} important`}
              aria-pressed={isStarred}
            >
              <Star
                className={cn(
                  "size-4",
                  isStarred && "fill-orange-500 text-orange-500",
                )}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>标注为重要</p>
          </TooltipContent>
        </Tooltip>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="size-8 p-0"
            aria-label="More actions"
          >
            <span className="sr-only">打开菜单</span>
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
          <DropdownMenuLabel>操作</DropdownMenuLabel>
          <DropdownMenuItem
            disabled={deleteTodo.isPending}
            onClick={handleView}
          >
            <Eye className="size-4" /> 查看
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={deleteTodo.isPending}
            onClick={handleEdit}
          >
            <Pencil className="size-4" /> 编辑
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={deleteTodo.isPending}
            onClick={handleDelete}
            className={cn(
              "text-destructive",
              optimisticOperation === "delete" &&
                "pointer-events-none opacity-50",
            )}
          >
            <Trash className="size-4" /> 删除
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmationDialog />
    </div>
  );
};
